import * as React from 'react';
import { COLORS } from '../styles';
import { Attention, ChevronDown, ChevronUp, Information, ScrollArrowDown, ScrollArrowUp } from '../icons';
import { LoadingIndicator } from '../LoadingIndicator';
import { DropdownButton, Dropdown, DropdownContent, Loading, StyledField } from './CommonStyling';
import { AutofilledMessage, WarningMessage } from '../InputFields/styling';

interface DropdownFilterProps {
  id: string;
  list: string[];
  placeholder?: string;
  onSelect?: (value: string) => void;
  initalValue?: string;
  disableSorting?: boolean;
  messageOnNoResults?: string;
  disabled?: boolean;
  locked?: boolean;
  isButton?: boolean;
  activeValidationMessage?: string;
  autofilledMessage?: string;
}

const BasicDropdown = ({
  id,
  list,
  placeholder,
  onSelect,
  initalValue,
  disableSorting,
  messageOnNoResults,
  disabled,
  locked,
  isButton,
  activeValidationMessage,
  autofilledMessage,
}: DropdownFilterProps) => {
  const [isOpen, setIsOpen] = React.useState<Boolean>(false);
  const [isLoading, setIsLoading] = React.useState<Boolean>(false);
  const [input, setInput] = React.useState<string>('');
  const [placeholderSearch, setPlaceholderSearch] = React.useState<string>(placeholder || '');
  const [restartFilter, setRestartFilter] = React.useState<boolean>(false);
  const [focused, setFocused] = React.useState<number | null>(null);

  const dropdownContentRef = React.useRef<HTMLDivElement>(null);
  const styledFieldRef = React.useRef<HTMLDivElement>(null);

  const handleClickOutside = (e: any) => {
    if (dropdownContentRef?.current && !dropdownContentRef.current.contains(e.target)) {
      if (isOpen) {
        setIsOpen(false);
        setFocused(null);
        if (!list.includes(input)) {
          setInput('');
          setPlaceholderSearch(placeholder || '');
        }
      }
    }
  };

  const setNewFocusedElement = (index: number) => {
    const newFocusedElement = document.getElementById(`${id}_${index}`);
    if (newFocusedElement) {
      setFocused(index);
      newFocusedElement.focus();
    }
  };

  const handleKeyDown = (e: any) => {
    if (isOpen) {
      if (e.keyCode === 38) {
        e.preventDefault();
        var focusedNow = focused;
        if (focusedNow !== undefined && focusedNow !== null && focusedNow > 0) {
          focusedNow -= 1;
          setNewFocusedElement(focusedNow);
        }
      } else if (e.keyCode === 40) {
        e.preventDefault();
        var focusedNow = focused;
        if (focusedNow !== undefined && focusedNow !== null) {
          focusedNow++;
        } else {
          focusedNow = 0;
        }
        setNewFocusedElement(focusedNow);
      } else if (e.keyCode === 9) {
        e.preventDefault();
        var focusedNow = focused;
        if (focusedNow !== undefined && focusedNow !== null) {
          focusedNow++;
          const newFocusedElement = document.getElementById(`${id}_${focusedNow}`);
          if (!newFocusedElement) {
            setNewFocusedElement(0);
          } else {
            setNewFocusedElement(focusedNow);
          }
        } else {
          setNewFocusedElement(0);
        }
      } else if (e.keyCode === 27) {
        setIsOpen(false);
        setNewFocusedElement(0);
      }
    }
  };

  const handleKeyPress = (e: any) => {
    if (styledFieldRef?.current && styledFieldRef.current.contains(e.target)) {
      if (e.keyCode === 13) {
        setIsOpen(!isOpen);
        setNewFocusedElement(0);
      } else if (e.keyCode === 40) {
        setIsOpen(!isOpen);
        setNewFocusedElement(0);
      }
    }
  };

  React.useEffect(() => {
    if (initalValue) {
      setInput(initalValue);
      setRestartFilter(true);
    }
  }, [initalValue]);

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keypress', handleKeyPress);
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keypress', handleKeyPress);
      document.removeEventListener('click', handleClickOutside);
    };
  });

  React.useEffect(() => {
    setIsLoading(false);
  }, [input]);

  const getElements = () => {
    var number = 0;
    var options = list;
    if (!restartFilter && input !== '') {
      options = options.filter(option => option.toUpperCase().indexOf(input.toUpperCase()) !== -1);
    }
    if (!disableSorting) {
      options = options.sort();
    }
    if (options.length === 0) {
      return (
        <DropdownButton disabled>
          <span>{messageOnNoResults}</span>
        </DropdownButton>
      );
    }
    return options.map(item => {
      return (
        <DropdownButton
          type="button"
          onClick={(e: any) => {
            e.preventDefault();
            setRestartFilter(true);
            setInput(item);
            onSelect && onSelect(item);
            setIsOpen(false);
            setFocused(null);
          }}
          className={item === input ? 'active' : ''}
          key={item}
          id={`${id}_${number++}`}>
          <span>{item}</span>
        </DropdownButton>
      );
    });
  };

  return (
    <>
      <Dropdown isButton={isButton || false} locked={locked} disabled={disabled}>
        <StyledField
          ref={styledFieldRef}
          className={isOpen ? 'expanded' : ''}
          onClick={() => {
            if (!locked || !disabled) {
              setIsOpen(!isOpen);
            }
          }}
          tabIndex={disabled || locked ? -1 : 0}
          isButton={isButton || false}
          disabled={disabled || false}
          locked={locked || false}
          showValidationMessage={!!activeValidationMessage}
          isPlaceholder={!input}>
          {input || placeholderSearch}
          {isLoading ? (
            <Loading>
              <LoadingIndicator size="small" color={COLORS.neutral_600} />
            </Loading>
          ) : null}
          <div onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? isButton ? <ScrollArrowUp size="24px" /> : <ChevronUp size="24px" /> : isButton ? <ScrollArrowDown size="24px" /> : <ChevronDown size="24px" />}
          </div>
        </StyledField>
        {!locked && !disabled && (
          <DropdownContent isButton={isButton || false} ref={dropdownContentRef} id={`${id}_dropdowncontent`} className={isOpen ? 'show' : ''}>
            {getElements()}
          </DropdownContent>
        )}
      </Dropdown>
      {activeValidationMessage && (
        <WarningMessage>
          <Attention size="20px" color={COLORS.warning_400} />
          <span>{activeValidationMessage}</span>
        </WarningMessage>
      )}
      {autofilledMessage && (
        <AutofilledMessage>
          <Information size="20px" color={COLORS.neutral_600} />
          <span>{autofilledMessage}</span>
        </AutofilledMessage>
      )}
    </>
  );
};

export default BasicDropdown;
